import { createAsyncThunk } from "@reduxjs/toolkit";
import FIND_CASE_API from "./CaseDetailsApi";
import OOT from "dot/Constants/services/oot";
import { RootState } from "Store/mainStore";
import { 
  resetAuth, 
  setOOTStatus, 
  setOOTAuth, 
  showLoader,
  setError 
} from "Store/auth";
import { resetCD, setStatus } from "Store/contentState/caseDetailsSlice";
import { setDataFC } from "Store/contentState/findCaseSlice";
import { STATUS, nonSCRMCaseTypes, sCRMRegex } from "dot/Components/Misc/CaseDetails_System";
import {
  processOpportunitySuccess,
  processOpportunityFailure,
  processOpportunityException,
  prepareScrmId,
  OpportunityProcessorContext,
} from "../utils/opportunityProcessor";
import { checkIfCaseType_subType_Exists } from "dot/Components/Logics/ensoCommonFunctions";

/**
 * Fetch Opportunity Data
 * 
 * This thunk fetches opportunity data from the OOT (Opportunity) service.
 * It's a generic, reusable thunk that can be called from multiple components
 * with different processing options.
 * 
 * @param params - Object containing:
 *   - scrmId: The sCRM ID to search for
 *   - caseType: The selected case type
 *   - subType: The selected sub type
 *   - fullProcessing: Whether to run all business logic (default: true)
 *   - skipPreparation: Skip sCRM ID validation and preparation (default: false)
 */
export const fetchOpportunity = createAsyncThunk<
  any,
  {
    scrmId: string;
    caseType: string;
    subType: string;
    fullProcessing?: boolean;
    skipPreparation?: boolean;
  },
  { state: RootState }
>(
  'findCase/fetchOpportunity',
  async (params, thunkAPI) => {
    const { 
      scrmId, 
      caseType, 
      subType, 
      fullProcessing = true, 
      skipPreparation = false 
    } = params;
    
    const { dispatch, getState } = thunkAPI;
    const state = getState();
    const authSelector = state.authSlice;

    try {
      // ========================================
      // STEP 1: Initialize and Reset State
      // ========================================
      if (fullProcessing) {
        dispatch(resetAuth());
        dispatch(resetCD());
      }

      // ========================================
      // STEP 2: Prepare sCRM ID
      // Validate and prepare the sCRM ID for the API call
      // ========================================
      let preparedScrmId = scrmId;
      let noscrmid = false;

      if (!skipPreparation) {
        const prepResult = prepareScrmId(scrmId, caseType, sCRMRegex, dispatch);
        preparedScrmId = prepResult.scrmId;
        noscrmid = prepResult.noscrmid;
      }

      // Handle non-sCRM case types
      if (nonSCRMCaseTypes.includes(caseType)) {
        dispatch(setDataFC({ key: "sCRMID", value: "" }));
        dispatch(setStatus(STATUS.IDLE));
      } else {
        dispatch(setDataFC({ key: "sCRMID", value: preparedScrmId }));
        dispatch(setStatus(STATUS.IDLE));
      }

      // ========================================
      // STEP 3: Set Loading State
      // ========================================
      dispatch(setStatus(STATUS.LOADING));
      dispatch(setOOTStatus(STATUS.LOADING));
      dispatch(setOOTAuth({ data: {} }));
      dispatch(showLoader(true));

      // ========================================
      // STEP 4: Call OOT API
      // ========================================
      const response = await OOT.getOpportunity({ scrm_id: preparedScrmId });

      // ========================================
      // STEP 5: Process Response
      // ========================================
      const caseTypeSubType = checkIfCaseType_subType_Exists(state?.findCase,caseType, subType);

      const context: OpportunityProcessorContext = {
        caseType,
        subType,
        scrmId: preparedScrmId,
        noscrmid,
        userRole: authSelector.userDatamart_role,
        fullProcessing,
        caseSubType: caseTypeSubType?.sub_types?.find((item) => item.code === subType)
      };

      if (response.status) {
        // Success - process the response
        processOpportunitySuccess(response, context, dispatch, state);
      } else {
        // API returned failure status
        processOpportunityFailure(response, dispatch);
      }

      // ========================================
      // STEP 6: Cleanup and Reset
      // ========================================
      dispatch(showLoader(false));
      // dispatch(setError("reset"));

      return response;
      
    } catch (error: any) {
      // ========================================
      // Exception Handling (Network errors, timeouts, etc.)
      // ========================================
      processOpportunityException(error, dispatch);
      dispatch(showLoader(false));
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch opportunity');
    }
  }
);

// Fetch case list
export const fetchCaseList = createAsyncThunk(
  'findCase/fetchCaseList',
  async (_, thunkAPI) => {
    try {
      const caseTypeList = await FIND_CASE_API.getCaseDetails();
      const resp = {
        status:true,
        data: caseTypeList?.data || []
      };

      if (!resp?.["status"]) {
        return thunkAPI.rejectWithValue(resp?.["message"] || 'Failed to fetch case list');
      }
      return resp;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Unknown error');
    }
  }
);
